import { Component, OnDestroy, OnInit } from '@angular/core';
import { MlAdminOperationsSnapshot } from '../../../_core/_model/machine-learning';
import { MachineLearningOperationsOverviewService } from '../../../_core/_services/machine-learning-operations-overview.service';
import { AuthService } from '../../../_core/_services/auth.service';
import { Subscription } from 'rxjs';
import { UserRole } from '../../../_core/_model/user-role';

@Component({
  selector: 'app-admin-machine-learning-nav',
  standalone: false,
  templateUrl: './admin-machine-learning-nav.html',
  styleUrl: './admin-machine-learning-nav.css'
})
export class AdminMachineLearningNav implements OnInit, OnDestroy {
  snapshot: MlAdminOperationsSnapshot | null = null;
  currentRole: UserRole | null = null;
  private sub = new Subscription();

  constructor(
    private overviewService: MachineLearningOperationsOverviewService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.sub.add(this.auth.currentUser$.subscribe(user => {
      this.currentRole = (user?.role as UserRole | undefined) ?? null;

      if (this.isAdmin && !this.snapshot) {
        this.sub.add(this.overviewService.watchSnapshot().subscribe({
          next: snapshot => this.snapshot = snapshot,
          error: () => this.snapshot = null
        }));
      }
    }));

    this.auth.getCurrentUser().subscribe();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get isAdmin(): boolean {
    return this.currentRole === 'Admin' || this.currentRole === 'SuperAdmin';
  }

  get canCompliance(): boolean {
    return this.isAdmin || this.currentRole === 'Compliance';
  }

  get canFinance(): boolean {
    return this.isAdmin || this.currentRole === 'Finance';
  }

  get canComplianceOrFinance(): boolean {
    return this.canCompliance || this.canFinance;
  }

  get lifecycleBadge(): number {
    return this.snapshot?.lifecycle?.actionItems.length ?? 0;
  }

  get governanceBadge(): number {
    const summary = this.snapshot?.candidateGovernance;
    return (summary?.pendingReview ?? 0) + (summary?.promotionReady ?? 0);
  }

  get withdrawalBadge(): number {
    const summary = this.snapshot?.withdrawalRisk;
    return (summary?.pending ?? 0) + (summary?.submitting ?? 0) + (summary?.reconciliationRequired ?? 0);
  }

  get depositBadge(): number {
    const summary = this.snapshot?.depositRisk;
    return (summary?.pending ?? 0)
      + (summary?.inReview ?? 0)
      + (summary?.sourceOfFundsRequested ?? 0)
      + (summary?.escalated ?? 0);
  }

  get reliabilityBadge(): number {
    return this.snapshot?.operationalAlerts.length ?? 0;
  }

  get liquidityBadge(): number {
    return this.snapshot?.liquidity?.criticalCorridors ?? 0;
  }

  get deploymentBadge(): number {
    const health = this.snapshot?.deploymentHealth;
    if (!health) return 0;
    return health.recommendRollback || health.healthStatus === 'Critical'
      ? Math.max(1, health.alerts.length)
      : 0;
  }
}
